import { z } from 'zod';

import {
  activityDimensions,
  activityDimensionValues,
  activityObservedDimensions,
  hasAnyDimensionValue,
} from './activity-dimensions';

// ============================================================================
// ENUMS
// ============================================================================

// Mirrors the Prisma `ActivityEventTrigger` enum. `scheduled` rows came from
// the per-patient cron tick; `motion` rows came from a near-real-time Axis/
// ONVIF motion webhook (debounced per patient).
export const activityEventTriggerEnum = z.enum(['scheduled', 'motion']);
export type ActivityEventTrigger = z.infer<typeof activityEventTriggerEnum>;

// ============================================================================
// ACTIVITY MODEL REGISTRY (per-patient selection)
// ============================================================================

// Models an operator may assign to a patient for activity classification.
// Capability and pricing live on the entry so the classifier, the cost cap and
// the web app read one table; an id that is not here is unknown everywhere.
//
// NOTE: the `id` values must match the Bedrock model / inference-profile ids
// enabled in the AWS account. Verify before shipping.
export const activityModelProviderKind = z.enum(['bedrock-converse', 'bedrock-invoke-pegasus']);
export type ActivityModelProviderKind = z.infer<typeof activityModelProviderKind>;

export const activityModelInputMode = z.enum(['frames', 'video']);
export type ActivityModelInputMode = z.infer<typeof activityModelInputMode>;

// USD per 1M tokens; `videoPerSecond` is USD per input video second for
// models that bill by duration (Pegasus reports no token counts at all).
export const activityModelPricing = z.object({
  inputPerMTok: z.number().nonnegative(),
  outputPerMTok: z.number().nonnegative(),
  videoPerSecond: z.number().nonnegative().optional(),
});
export type ActivityModelPricing = z.infer<typeof activityModelPricing>;

export const activityModelEntry = z.object({
  id: z.string(),
  label: z.string(),
  provider: activityModelProviderKind,
  inputMode: activityModelInputMode,
  /**
   * Models that can only run the merged strategy. TwelveLabs Pegasus needs a
   * media source on every call, so it cannot make the text-only stage-2
   * decision the per-camera path depends on — the backend forces `merged`
   * for these regardless of the patient's configured strategy.
   */
  mergedOnly: z.boolean(),
  pricing: activityModelPricing,
});
export type ActivityModelEntry = z.infer<typeof activityModelEntry>;

const CLAUDE_HAIKU_4_5: ActivityModelEntry = {
  id: 'us.anthropic.claude-haiku-4-5-20251001-v1:0',
  label: 'Claude Haiku 4.5',
  provider: 'bedrock-converse',
  inputMode: 'frames',
  mergedOnly: false,
  pricing: { inputPerMTok: 1.1, outputPerMTok: 5.5 },
};

export const ACTIVITY_MODELS: readonly ActivityModelEntry[] = [
  CLAUDE_HAIKU_4_5,
  {
    id: 'us.anthropic.claude-sonnet-4-6',
    label: 'Claude Sonnet 4.6',
    provider: 'bedrock-converse',
    inputMode: 'frames',
    mergedOnly: false,
    pricing: { inputPerMTok: 3.3, outputPerMTok: 16.5 },
  },
  {
    id: 'us.amazon.nova-2-lite-v1:0',
    label: 'Amazon Nova 2 Lite',
    provider: 'bedrock-converse',
    inputMode: 'video',
    mergedOnly: false,
    pricing: { inputPerMTok: 0.33, outputPerMTok: 2.75 },
  },
  {
    id: 'us.amazon.nova-pro-v1:0',
    label: 'Amazon Nova Pro',
    provider: 'bedrock-converse',
    inputMode: 'video',
    mergedOnly: false,
    pricing: { inputPerMTok: 0.8, outputPerMTok: 3.2 },
  },
  // Dedicated video-understanding model. InvokeModel-only (no Converse), billed
  // per input video second rather than per input token, and merged-only.
  // us-east-2 has no in-region endpoint, hence the `us.` geo inference profile.
  {
    id: 'us.twelvelabs.pegasus-1-2-v1:0',
    label: 'TwelveLabs Pegasus 1.2',
    provider: 'bedrock-invoke-pegasus',
    inputMode: 'video',
    mergedOnly: true,
    pricing: { inputPerMTok: 0, outputPerMTok: 7.5, videoPerSecond: 0.00049 },
  },
];

export const ACTIVITY_MODEL_IDS: readonly string[] = ACTIVITY_MODELS.map((m) => m.id);

/** Default model assigned to every patient (mirrors the Prisma column default). */
export const DEFAULT_ACTIVITY_MODEL: ActivityModelEntry = CLAUDE_HAIKU_4_5;
export const DEFAULT_ACTIVITY_MODEL_ID = DEFAULT_ACTIVITY_MODEL.id;

export function getActivityModel(id: string): ActivityModelEntry | undefined {
  return ACTIVITY_MODELS.find((m) => m.id === id);
}

export function supportsMergedStrategy(model: Pick<ActivityModelEntry, 'inputMode'>): boolean {
  return model.inputMode === 'video';
}

export function supportsPerCameraStrategy(model: Pick<ActivityModelEntry, 'mergedOnly'>): boolean {
  return !model.mergedOnly;
}

export const activityModelsResponse = z.object({
  items: z.array(activityModelEntry).readonly(),
  defaultId: z.string(),
});
export type ActivityModelsResponse = z.infer<typeof activityModelsResponse>;

// ============================================================================
// CLASSIFICATION STRATEGY (per-patient)
// ============================================================================

// Mirrors the Prisma `ActivityClassifyStrategy` enum. A patient's choice is a
// preference, not a guarantee: the backend forces `merged` on a merged-only
// model (Pegasus) and falls back to `per_camera` on a non-video model.
// `vision` skips Bedrock and classifies through the activity-inference service.
export const activityClassifyStrategy = z.enum(['per_camera', 'merged', 'vision']);
export type ActivityClassifyStrategy = z.infer<typeof activityClassifyStrategy>;

// Mirrors the Prisma `ActivityMergeLayout` enum. Only read when the resolved
// strategy is `merged`.
export const activityMergeLayout = z.enum(['grid', 'sequence']);
export type ActivityMergeLayout = z.infer<typeof activityMergeLayout>;

/** Defaults assigned to every patient (mirror the Prisma column defaults). */
export const DEFAULT_ACTIVITY_CLASSIFY_STRATEGY: ActivityClassifyStrategy = 'per_camera';
export const DEFAULT_ACTIVITY_MERGE_LAYOUT: ActivityMergeLayout = 'grid';

// ============================================================================
// BODY SCHEMAS
// ============================================================================

// Body for `PUT /patients/:id/activity-config`. The on/off checkbox lives in
// `enabled`; the cadence in `intervalMinutes`. Toggling `enabled: false`
// preserves the cadence so resume keeps the operator's prior choice.
//
// Cadence floor: 3 min (worker latency budget + Bedrock TPM caution).
// Recommended default: 5 min.
// Cap: 60 min — anything above silently disables capture, so we reject loud.
export const MIN_ACTIVITY_CLIP_DURATION_SECONDS = 8;
export const MAX_ACTIVITY_CLIP_DURATION_SECONDS = 30;
export const MAX_ACTIVITY_CONTEXT_LENGTH = 2000;

export const updateActivityConfigBody = z
  .object({
    enabled: z.boolean(),
    intervalMinutes: z.number().int().min(3).max(60).nullable().optional(),
    clipDurationSeconds: z
      .number()
      .int()
      .min(MIN_ACTIVITY_CLIP_DURATION_SECONDS)
      .max(MAX_ACTIVITY_CLIP_DURATION_SECONDS)
      .nullable()
      .optional(),
    aiContext: z.string().max(MAX_ACTIVITY_CONTEXT_LENGTH).nullable().optional(),
    dailyCostCapUsd: z.number().nonnegative().nullable().optional(),
    /** Bedrock model id for this patient. Omit to leave unchanged. */
    modelId: z
      .string()
      .refine((v) => ACTIVITY_MODEL_IDS.includes(v), 'Unsupported model')
      .optional(),
    /** How this patient's captures reach the model. Omit to leave unchanged. */
    classifyStrategy: activityClassifyStrategy.optional(),
    /** Composite shape when the strategy resolves to `merged`. Omit to leave unchanged. */
    mergeLayout: activityMergeLayout.optional(),
  })
  .superRefine((val, ctx) => {
    if (val.enabled && (val.intervalMinutes == null)) {
      ctx.addIssue({
        code: 'custom',
        path: ['intervalMinutes'],
        message: 'intervalMinutes is required when enabled is true',
      });
    }
    if (val.enabled && (val.clipDurationSeconds == null)) {
      ctx.addIssue({
        code: 'custom',
        path: ['clipDurationSeconds'],
        message: 'clipDurationSeconds is required when enabled is true',
      });
    }
    if (val.dailyCostCapUsd != null && val.dailyCostCapUsd <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['dailyCostCapUsd'],
        message: 'dailyCostCapUsd must be greater than 0 (leave blank for no cap)',
      });
    }
  });
export type UpdateActivityConfigBody = z.infer<typeof updateActivityConfigBody>;

export const activityReviewVerdict = z.enum(['accepted', 'rejected']);
export type ActivityReviewVerdict = z.infer<typeof activityReviewVerdict>;

// A rejection carries a corrected code, dimension overrides, or both. With
// dimensions only, the server resolves the code through the same rules the
// capture used; with both, the corrected code is stored and a disagreement with
// the resolver is flagged in `metadata.reviewConflict`. The stored review status
// records whether the AI's code stood, whatever the verdict sent.
export const reviewActivityEventBody = z
  .object({
    verdict: activityReviewVerdict,
    correctCodeId: z.uuid().optional(),
    dimensions: activityDimensionValues.optional(),
    notes: z.string().max(1000).optional(),
    metadata: z
      .array(
        z.object({
          codeMetadataId: z.uuid(),
          codeMetadataOptionId: z.uuid().optional(),
          notes: z.string().max(1000).optional(),
        }),
      )
      .optional(),
  })
  .superRefine((v, ctx) => {
    const hasDimensions = hasAnyDimensionValue(v.dimensions);
    if (v.verdict === 'rejected' && !v.correctCodeId && !hasDimensions) {
      ctx.addIssue({
        code: 'custom',
        path: ['correctCodeId'],
        message: 'correctCodeId or dimensions are required when rejecting',
      });
    }
    if (v.verdict === 'accepted' && v.dimensions !== undefined) {
      ctx.addIssue({ code: 'custom', path: ['dimensions'], message: 'dimensions are only read when rejecting' });
    }
  });
export type ReviewActivityEventBody = z.infer<typeof reviewActivityEventBody>;

// ============================================================================
// QUERY SCHEMAS
// ============================================================================

// Paginated read for the activity timeline. `from` and `to` are MANDATORY —
// scanning a patient's entire indefinite-retention history is too expensive
// to be allowed at the API edge.
export const getActivityEventsQuery = z
  .object({
    /** Restrict to one patient. Omit only when paired with `reviewStatus`
     * (e.g. streams-page hydration of pending events across visible patients). */
    patientId: z.uuid().optional(),
    from: z.iso.datetime({ offset: true }),
    to: z.iso.datetime({ offset: true }),
    /** Optional filter — restrict to a single Code. */
    codeId: z.uuid().optional(),
    /** Optional filter — restrict to a single CodeCategory (joins Code.categoryId). */
    categoryId: z.uuid().optional(),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    /** Optional filter — `accepted` | `rejected` | `pending` (null reviewStatus). */
    reviewStatus: z.enum(['accepted', 'rejected', 'pending']).optional(),
  })
  .superRefine((val, ctx) => {
    if (Date.parse(val.to) <= Date.parse(val.from)) {
      ctx.addIssue({
        code: 'custom',
        path: ['to'],
        message: '`to` must be after `from`',
      });
    }
  });
export type GetActivityEventsQuery = z.infer<typeof getActivityEventsQuery>;

// ============================================================================
// RESPONSE SCHEMAS
// ============================================================================

// Embedded Code reference returned with each ActivityEvent. Includes the
// optional CodeCategory so the UI can color-code rows without a follow-up
// fetch. Pulled from the same row as the activity event via Prisma include.
export const activityEventCodeSummary = z.object({
  id: z.string(),
  code: z.string(),
  description: z.string(),
  category: z
    .object({
      id: z.string(),
      name: z.string(),
      color: z.string(),
    })
    .nullable()
    .optional(),
});
export type ActivityEventCodeSummary = z.infer<typeof activityEventCodeSummary>;

// Row shape returned by `GET /activity/events`. Mirrors the Prisma row but
// keeps `metadata` as `unknown` — its keys (modelId, latencyMs, stages,
// attemptedCameraIds) are an internal contract subject to change without API
// versioning churn.
export const activityEventCameraSummary = z.object({
  id: z.string(),
  name: z.string(),
});
export type ActivityEventCameraSummary = z.infer<typeof activityEventCameraSummary>;

// One person the classifier saw in one camera's view. `label` ("Person A") is
// scoped to that view: the same label on another camera, or on the next tick,
// carries no claim that it is the same individual.
export const activityPersonSighting = z.object({
  cameraId: z.string(),
  cameraName: z.string(),
  lighting: z.string(),
  label: z.string(),
  description: z.string(),
  confidence: z.number(),
  /** Observable dimensions stage 1 reported for this person; null for events written before prompt v7. */
  dimensions: activityObservedDimensions.nullable().optional(),
});
export type ActivityPersonSighting = z.infer<typeof activityPersonSighting>;

// Which suggestions a monitor should look at first. Computed server-side from
// the AI's dimension confidences, resolver/model disagreement and a short list
// of codes that always warrant a look (see web-api `review-priority.ts`).
export const activityReviewPriority = z.enum(['high', 'normal']);
export type ActivityReviewPriority = z.infer<typeof activityReviewPriority>;

export const activityEventReviewLog = z.object({
  id: z.string(),
  code: activityEventCodeSummary,
  notes: z.string().nullable().optional(),
});
export type ActivityEventReviewLog = z.infer<typeof activityEventReviewLog>;

export const activityEventResponse = z.object({
  id: z.string(),
  createdAt: z.date(),
  patientId: z.string(),
  cameraId: z.string(),
  camera: activityEventCameraSummary,
  scheduledAt: z.date(),
  capturedAt: z.date(),
  code: activityEventCodeSummary,
  confidence: z.number(),
  routingReason: z.string(),
  attemptCount: z.number().int(),
  trigger: activityEventTriggerEnum,
  metadata: z.unknown(),
  /** S3 object key of the attributed camera's archived MP4 clip. */
  videoS3Key: z.string().nullable().optional(),
  /** How many recordings fed this decision. 0 for events written before captures were stored. */
  capturesCount: z.number().int(),
  /** Everyone seen across every camera on this tick, flattened for the timeline. */
  people: z.array(activityPersonSighting),
  /** The patient's dimensions as the AI emitted them; null for events written before prompt v7. */
  dimensions: activityDimensions.nullable(),
  /** The dimensions the reviewer asserted (the AI's on accept); null until reviewed, or for a code-only correction. */
  reviewedDimensions: activityDimensions.nullable(),
  reviewPriority: activityReviewPriority,
  reviewStatus: activityReviewVerdict.nullable().optional(),
  reviewedAt: z.date().nullable().optional(),
  reviewedById: z.string().nullable().optional(),
  reviewLog: activityEventReviewLog.nullable().optional(),
  deleted: z.date().nullable().optional(),
});
export type ActivityEventResponse = z.infer<typeof activityEventResponse>;

export const activityEventListResponse = z.object({
  items: z.array(activityEventResponse),
  nextCursor: z.string().nullable(),
});
export type ActivityEventListResponse = z.infer<typeof activityEventListResponse>;

// ============================================================================
// PARAMS / RESPONSE: GET /activity/events/:id/video-url
// ============================================================================

export const activityEventIdParam = z.object({ id: z.uuid() });
export type ActivityEventIdParam = z.infer<typeof activityEventIdParam>;

export const activityEventVideoUrlResponse = z.object({
  url: z.string(),
  expiresAt: z.iso.datetime({ offset: true }),
});
export type ActivityEventVideoUrlResponse = z.infer<typeof activityEventVideoUrlResponse>;

// ============================================================================
// RESPONSE: GET /activity/events/:id/captures
// ============================================================================

// Every recording that fed one tick's decision, each with a short-lived
// playback URL. Lets a monitor check the rooms the AI did NOT attribute the
// code to — the whole point of sweeping every camera.
export const activityCaptureResponse = z.object({
  id: z.string(),
  cameraId: z.string(),
  camera: activityEventCameraSummary,
  /** 'normal' | 'ir' — an IR twin is a second look at the same viewpoint. */
  lighting: z.string(),
  /** 'relay' | 'hls' | 'rtsp' */
  source: z.string(),
  capturedAt: z.date(),
  /** The capture the event's code was drawn from. Exactly one per event. */
  isAttributed: z.boolean(),
  peopleCount: z.number().int().nullable(),
  people: z.array(activityPersonSighting),
  /** One-sentence description of the view, including empty rooms. */
  sceneSummary: z.string().nullable(),
  /** Null when the clip was never archived (upload disabled or failed). */
  url: z.string().nullable(),
});
export type ActivityCaptureResponse = z.infer<typeof activityCaptureResponse>;

export const activityEventCapturesResponse = z.object({
  items: z.array(activityCaptureResponse),
  /**
   * The composite the model was actually shown, when the `merged` strategy ran.
   * Null on the per-camera path. Worth surfacing because it is the only way to
   * judge whether a camera shrunk into a tile was still legible enough to code.
   */
  mergedUrl: z.string().nullable(),
  /** 'grid' | 'sequence' — how the composite was laid out. Null when not merged. */
  mergedLayout: z.string().nullable(),
  /**
   * The per-view label ("Person A") of the person the AI took as the patient.
   * Labels are scoped to one camera's view, so it only names someone on the
   * attributed capture. Null for events written before stage 2 reported it.
   */
  personLabel: z.string().nullable(),
  /** When the playback URLs stop working; the client refetches past this. */
  expiresAt: z.iso.datetime({ offset: true }),
});
export type ActivityEventCapturesResponse = z.infer<typeof activityEventCapturesResponse>;

// ============================================================================
// GET /activity/usage — aggregated Bedrock token / latency stats from the
// metadata column on ActivityEvent. Groups by patient × day × modelId.
// ============================================================================

export const getActivityUsageQuery = z
  .object({
    from: z.iso.datetime({ offset: true }),
    to: z.iso.datetime({ offset: true }),
    patientId: z.uuid().optional(),
    /** Bucket granularity: `day` → YYYY-MM-DD, `month` → YYYY-MM. */
    granularity: z.enum(['day', 'month']).default('day'),
  })
  .superRefine((val, ctx) => {
    if (Date.parse(val.to) <= Date.parse(val.from)) {
      ctx.addIssue({ code: 'custom', path: ['to'], message: '`to` must be after `from`' });
    }
  });
export type GetActivityUsageQuery = z.infer<typeof getActivityUsageQuery>;

export const activityUsageItem = z.object({
  patientId: z.string(),
  /** Period bucket — `YYYY-MM-DD` for daily, `YYYY-MM` for monthly. */
  day: z.string(),
  patientName: z.string().optional(),
  modelId: z.string(),
  events: z.number().int(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  avgLatencyMs: z.number().int(),
  /** Estimated cost in USD based on current Bedrock list pricing. Per-model
   * rates change occasionally so this is approximate, not for billing. */
  costUsd: z.number(),
});
export type ActivityUsageItem = z.infer<typeof activityUsageItem>;

export const activityUsageResponse = z.object({
  items: z.array(activityUsageItem),
  /** The per-patient monthly budget (server config) a month's `costUsd` is compared against. */
  monthlyTargetUsd: z.number(),
});
export type ActivityUsageResponse = z.infer<typeof activityUsageResponse>;

// ============================================================================
// GET /patients/:id/activity-cost-estimate — what the patient's check-in
// cadence would cost at a given configuration. Every field is optional so the
// config form can price a draft; anything omitted falls back to the stored
// config.
// ============================================================================

export const getActivityCostEstimateQuery = z.object({
  intervalMinutes: z.coerce.number().int().min(3).max(60).optional(),
  clipDurationSeconds: z.coerce
    .number()
    .int()
    .min(MIN_ACTIVITY_CLIP_DURATION_SECONDS)
    .max(MAX_ACTIVITY_CLIP_DURATION_SECONDS)
    .optional(),
  modelId: z
    .string()
    .refine((v) => ACTIVITY_MODEL_IDS.includes(v), 'Unsupported model')
    .optional(),
  classifyStrategy: activityClassifyStrategy.optional(),
  mergeLayout: activityMergeLayout.optional(),
});
export type GetActivityCostEstimateQuery = z.infer<typeof getActivityCostEstimateQuery>;

/** Where `costPerTickUsd` came from: the patient's own recent ticks, or the static per-model table. */
export const activityCostPerTickSource = z.enum(['empirical', 'table']);
export type ActivityCostPerTickSource = z.infer<typeof activityCostPerTickSource>;

export const activityCostEstimateResponse = z.object({
  /** Ticks the scheduler would fire per day, averaged over the patient's upcoming CHECK_IN slots. */
  ticksPerDay: z.number(),
  costPerTickUsd: z.number(),
  costPerTickSource: activityCostPerTickSource,
  costPerDayUsd: z.number(),
  costPerMonthUsd: z.number(),
  /** The per-patient monthly budget (server config). */
  targetUsd: z.number(),
  overTarget: z.boolean(),
});
export type ActivityCostEstimateResponse = z.infer<typeof activityCostEstimateResponse>;

// ============================================================================
// GET /activity/accuracy — agreement between the AI code and the monitor's
// review, computed from reviewStatus and the review Log's code.
// ============================================================================

export const activityAccuracyGroupBy = z.enum([
  'code',
  'patient',
  'camera',
  'model',
  'promptVersion',
  'strategy',
  'lighting',
  'dimension',
]);
export type ActivityAccuracyGroupBy = z.infer<typeof activityAccuracyGroupBy>;

export const getActivityAccuracyQuery = z
  .object({
    from: z.iso.datetime({ offset: true }),
    to: z.iso.datetime({ offset: true }),
    patientId: z.uuid().optional(),
    cameraId: z.string().min(1).optional(),
    granularity: z.enum(['day', 'month']).default('month'),
    groupBy: activityAccuracyGroupBy.default('code'),
  })
  .superRefine((val, ctx) => {
    if (Date.parse(val.to) <= Date.parse(val.from)) {
      ctx.addIssue({ code: 'custom', path: ['to'], message: '`to` must be after `from`' });
    }
  });
export type GetActivityAccuracyQuery = z.infer<typeof getActivityAccuracyQuery>;

/**
 * One period × group bucket. For every `groupBy` but `dimension` a review counts
 * once, as the event's code-level verdict. For `dimension` the key is a
 * dimension name and each reviewed event that carries both the AI's and the
 * reviewer's dimension blocks counts once per dimension, accepted when the two
 * values match; events without both blocks stay out of dimension buckets.
 */
export const activityAccuracyItem = z.object({
  /** Period bucket — `YYYY-MM-DD` for daily, `YYYY-MM` for monthly. */
  period: z.string(),
  key: z.string(),
  label: z.string(),
  reviewed: z.number().int(),
  accepted: z.number().int(),
  rejected: z.number().int(),
  /** accepted ÷ reviewed, null when nothing in the bucket was reviewed. */
  agreement: z.number().nullable(),
});
export type ActivityAccuracyItem = z.infer<typeof activityAccuracyItem>;

export const activityAccuracyConfusionItem = z.object({
  aiCode: z.string(),
  reviewedCode: z.string(),
  count: z.number().int(),
});
export type ActivityAccuracyConfusionItem = z.infer<typeof activityAccuracyConfusionItem>;

export const activityAccuracyDimensionConfusionItem = z.object({
  /** One of `ACTIVITY_DIMENSION_NAMES`. */
  dimension: z.string(),
  aiValue: z.string(),
  reviewedValue: z.string(),
  count: z.number().int(),
});
export type ActivityAccuracyDimensionConfusionItem = z.infer<typeof activityAccuracyDimensionConfusionItem>;

export const activityAccuracyTotals = z.object({
  events: z.number().int(),
  reviewed: z.number().int(),
  unreviewed: z.number().int(),
  accepted: z.number().int(),
  rejected: z.number().int(),
  /** Code-level: accepted ÷ reviewed, whether or not the review carried dimensions. */
  agreement: z.number().nullable(),
  /** Reviewed events carrying both the AI's and the reviewer's dimension blocks. */
  reviewedWithDimensions: z.number().int(),
  /**
   * Reviews that changed `presence`: the AI coded someone other than the
   * patient, or missed a patient who was there. `rate` is over
   * `reviewedWithDimensions`, null when that is zero.
   */
  wrongPerson: z.object({
    events: z.number().int(),
    rate: z.number().nullable(),
  }),
});
export type ActivityAccuracyTotals = z.infer<typeof activityAccuracyTotals>;

export const activityAccuracyWorstEntry = z.object({
  key: z.string(),
  label: z.string(),
  reviewed: z.number().int(),
  agreement: z.number(),
});
export type ActivityAccuracyWorstEntry = z.infer<typeof activityAccuracyWorstEntry>;

/** Lowest agreement over the whole range, only among keys with at least `minReviewed` reviews. */
export const activityAccuracyWorst = z.object({
  minReviewed: z.number().int(),
  dimension: activityAccuracyWorstEntry.nullable(),
  camera: activityAccuracyWorstEntry.nullable(),
});
export type ActivityAccuracyWorst = z.infer<typeof activityAccuracyWorst>;

export const activityAccuracyResponse = z.object({
  totals: activityAccuracyTotals,
  items: z.array(activityAccuracyItem),
  /** Rejected events by (AI code → corrected code), most frequent first. */
  confusion: z.array(activityAccuracyConfusionItem),
  /** Per dimension, (AI value → reviewed value) where they differ, most frequent first. */
  dimensionConfusion: z.array(activityAccuracyDimensionConfusionItem),
  worst: activityAccuracyWorst,
});
export type ActivityAccuracyResponse = z.infer<typeof activityAccuracyResponse>;
